import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Cache for country configs
let countriesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fallback data if API fails
const FALLBACK_COUNTRIES = {
  usa: {
    code: 'usa', name: 'United States', currency: 'USD', price: 29.99,
    default_language: 'en-US', flag_emoji: '🇺🇸',
    supported_languages: ['en-US'],
    regions: ['California', 'Texas', 'Florida', 'New York', 'Illinois', 'Pennsylvania']
  },
  canada: {
    code: 'canada', name: 'Canada', currency: 'CAD', price: 39.99,
    default_language: 'en-CA', flag_emoji: '🇨🇦',
    supported_languages: ['en-CA', 'fr-CA'],
    regions: ['Ontario', 'Quebec', 'British Columbia', 'Alberta']
  },
  france: {
    code: 'france', name: 'France', currency: 'EUR', price: 29.99,
    default_language: 'fr-FR', flag_emoji: '🇫🇷',
    supported_languages: ['fr-FR'],
    regions: ['Île-de-France', 'Provence', 'Bretagne', 'Nouvelle-Aquitaine']
  },
  germany: {
    code: 'germany', name: 'Germany', currency: 'EUR', price: 29.99,
    default_language: 'de-DE', flag_emoji: '🇩🇪',
    supported_languages: ['de-DE'],
    regions: ['Bayern', 'Berlin', 'Nordrhein-Westfalen', 'Baden-Württemberg']
  },
  italy: {
    code: 'italy', name: 'Italy', currency: 'EUR', price: 29.99,
    default_language: 'it-IT', flag_emoji: '🇮🇹',
    supported_languages: ['it-IT'],
    regions: ['Lombardia', 'Lazio', 'Campania', 'Sicilia']
  },
  uk: {
    code: 'uk', name: 'United Kingdom', currency: 'GBP', price: 24.99,
    default_language: 'en-GB', flag_emoji: '🇬🇧',
    supported_languages: ['en-GB'],
    regions: ['England', 'Scotland', 'Wales', 'Northern Ireland']
  },
  australia: {
    code: 'australia', name: 'Australia', currency: 'AUD', price: 44.99,
    default_language: 'en-AU', flag_emoji: '🇦🇺',
    supported_languages: ['en-AU'],
    regions: ['New South Wales', 'Victoria', 'Queensland', 'Western Australia']
  },
  spain: {
    code: 'spain', name: 'Spain', currency: 'EUR', price: 24.99,
    default_language: 'es-ES', flag_emoji: '🇪🇸',
    supported_languages: ['es-ES'],
    regions: ['Madrid', 'Cataluña', 'Andalucía', 'Valencia']
  },
};

class CountryService {
  /**
   * Fetch all country configurations from API
   */
  async getAllCountries() {
    // Return cached data if still valid
    if (countriesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return countriesCache;
    }

    try {
      const response = await axios.get(`${API_URL}/countries/`);
      const countries = {};

      // Convert array to object keyed by code
      response.data.results.forEach(country => {
        countries[country.code] = {
          ...country,
          price: parseFloat(country.price), // Ensure price is a number
        };
      });

      countriesCache = countries;
      cacheTimestamp = Date.now();
      return countries;
    } catch (error) {
      console.error('Error fetching countries, using fallback:', error);
      return FALLBACK_COUNTRIES;
    }
  }

  /**
   * Get a specific country by code
   */
  async getCountryByCode(code) {
    const countries = await this.getAllCountries();
    return countries[code] || FALLBACK_COUNTRIES[code] || null;
  }

  /**
   * Get country synchronously from cache (for immediate UI needs)
   * Falls back to hardcoded values if not cached
   */
  getCountryFromCache(code) {
    if (countriesCache && countriesCache[code]) {
      return countriesCache[code];
    }
    return FALLBACK_COUNTRIES[code] || null;
  }

  /**
   * Clear the cache (useful after admin updates)
   */
  clearCache() {
    countriesCache = null;
    cacheTimestamp = null;
  }

  /**
   * Pre-fetch and cache all countries
   */
  async preload() {
    return this.getAllCountries();
  }
}

export default new CountryService();
